import React, {useState} from "react";
import Table from "@mui/material/Table";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import {
    Button,
    Chip,
    ListItemIcon,
    ListItemText,
    Menu,
    MenuItem,
    MenuList,
    styled,
    TableCell,
    TableContainer,
    Tooltip
} from "@mui/material";
import TableBody from "@mui/material/TableBody";
import {convertDateToTimeString} from "../utils/cert-helper";
import Box from "@mui/material/Box";
import {ApiUrl, PortalUrl} from "../utils/portal-urls";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelRoundedIcon from '@mui/icons-material/CancelRounded';
import RunCircleRoundedIcon from '@mui/icons-material/RunCircleRounded';
import ReportRoundedIcon from '@mui/icons-material/ReportRounded';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import {statusObj} from "../utils/customFunctions";
import PublishedHispsInfoDialog from "./PublishedHispsInfoDialog";
import {Link} from "react-router-dom";
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';

const StyledTableRow = styled(TableRow)(({theme}) => ({
    // hide last border
    '&:last-child td, &:last-child th': {
        border: 0,
    },
}));
const StyledTableCell = styled(TableCell)({
    fontWeight: 'bold'
})

const StyledSpan = styled('span')({
    padding: '10px'
})
const StyledDetailDiv = styled('div')({
    '& .value': {
        color: '#1769aa',
    }
})
export const DownloadFileButton = (props) => {
    const { title, url } = props;
    return (
        <>
            <Tooltip arrow title={`Download ${title}`} placement='left'>
                <Button sx={{textTransform: 'none'}}
                        href={url}
                        target='_blank'
                        startIcon={<FileDownloadIcon/>}
                        // size='small'
                >
                    {title}
                </Button>
            </Tooltip>
            <br/>
        </>
    )
}

/**
 * The audience-filtered Aggregated Directories — SOW #4, the 2026-06-17 split.
 *
 * Labelled with the PUBLISHABILITY values each contains, because the two overlap rather than partition:
 * a row marked 3 appears in both, so their row counts do not sum to the full directory. Keyed off the
 * publish record's own paths so a publish from before the split shows neither.
 *
 * Corrected 2026-08-26: the provider label read "PUBLISHABILITY 1, 2" and this comment said a row
 * marked 2 appears in both. That is the pre-2026-08-12 reading, in which 2 was the overlap. The enum
 * PublishAudience has said PROVIDERS = 1, 3 and CONSUMERS = 2, 3 since that correction, so the label
 * disagreed with what the button actually downloads -- and it disagreed in the disclosure-unsafe
 * direction, telling a reader that a consumers-only row reaches providers.
 */
export const audienceDownList = {
    'providers': {
        content: 'Provider Aggregated Directory (PUBLISHABILITY 1, 3)',
        pathField: 'outputProvidersDataPath',
        url: function (id) { return ApiUrl.Published.getAudienceDirectory.getPath(id, 'providers')},
    },
    'consumers': {
        content: 'Consumer Aggregated Directory (PUBLISHABILITY 2, 3)',
        pathField: 'outputConsumersDataPath',
        url: function (id) { return ApiUrl.Published.getAudienceDirectory.getPath(id, 'consumers')},
    },
}

export const reportDownList = {
    'detail': { content: 'Aggregated Detailed Report', url: function (id) { return ApiUrl.Published.getDetailedReport.getPath(id)}},
    'summaryField': { content: 'Aggregated Summary (Field) Report', url: function (id) { return ApiUrl.Published.getSummaryByFieldReport.getPath(id)}},
    'summaryRule': { content: 'Aggregated Summary (Rule) Report', url: function (id) { return ApiUrl.Published.getSummaryByRuleReport.getPath(id)}},
    'summary': { content: 'Aggregated Summary Report', url: function (id) { return ApiUrl.Published.getSummaryReport.getPath(id)}},
}


export default function PublishedAsList(props) {

    const publishedResult = props.publishedResult || [];
    const setSnackbarMsg = props.setSnackbarMsg;

    const [publishedHispsDialogOpen, setPublishedHispsDialogOpen] = useState(false);
    const [publishedHispsInfo, setPublishedHispsInfo] = useState(null);


    const handleStatusColor = (status) => {
        const statusIcon = () => {
            switch (status) {
                case 'Completed':
                    return <CheckCircleIcon/>
                case 'Init':
                case 'Running':
                    return <RunCircleRoundedIcon/>
                case 'Cancel':
                case 'Failed':
                    return <CancelRoundedIcon/>
                default:
                    return <ReportRoundedIcon/>
            }
        }

        const color = statusObj[status];
        if (color !== undefined) {
            return (
                <Chip icon={statusIcon(status)}
                      variant='outlined'
                      label={status.toUpperCase()}
                      color={color}
                />
            )
        }
    }

    const handlePublishedHispsDialogOpen = (info) => {
        setPublishedHispsInfo(info)
        setPublishedHispsDialogOpen(true);
    }

    return (
        <TableContainer sx={{mt: '10px'}}>
            <PublishedHispsInfoDialog
                setPublishedHispsDialogOpen={(c) => setPublishedHispsDialogOpen(c)}
                publishedHispsDialogOpen={publishedHispsDialogOpen}
                publishedHispsInfo={publishedHispsInfo}
                />
            <Table sx={{m: 0}}>
                <TableHead>
                    <TableRow>
                        <StyledTableCell>Publish Date (UTC) / Status</StyledTableCell>
                        <StyledTableCell>Publication Information</StyledTableCell>
                        <StyledTableCell sx={{ minWidth: 250}}>Failure Counts</StyledTableCell>
                        <StyledTableCell>Download</StyledTableCell>
                        <StyledTableCell>Details Information</StyledTableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {publishedResult && publishedResult.map(d => (

                        <StyledTableRow key={d.id}>
                            <TableCell>
                                <div style={{ display: 'flex', flexDirection: 'column', textAlign: 'center'}}>
                                    <StyledSpan>{convertDateToTimeString(d.publishedTs)}</StyledSpan>
                                    <StyledSpan>{handleStatusColor(d.status)}</StyledSpan>
                                    {/*<Button variant='text' color='primary' href={PortalUrl.Published.Publication.getPath(d.id)}>View Details</Button>*/}
                                    {/*<Button component={Link} variant='text' color='primary' to={PortalUrl.Published.Publication.getPath(d.id)}>View Details</Button>*/}
                                </div>
                            </TableCell>
                            <TableCell>
                                <StyledDetailDiv>
                                    <div><span className='value' style={{fontSize: 10}}>ID: {d.id}</span></div>
                                    HISP Count : <span className='value'>{d.hispCount}</span>
                                    <br/>
                                    Direct Address Count : <span className='value'>{d.directAddressCount}</span>
                                    <br/>
                                    Organization Count : <span className='value'>{d.organizationCount}</span>
                                    <br/>
                                    User Count : <span className='value'>{d.userCount}</span>
                                </StyledDetailDiv>
                            </TableCell>
                            <TableCell>
                                <StyledDetailDiv>
                                    Error Count : <span className='value'>{d.errorCount}</span>
                                    <br/>
                                    Tolerated Error Count : <span className='value'>{d.toleratedErrorCount}</span>
                                    <br/>
                                    Warning Count : <span className='value'>{d.warningCount}</span>
                                    <br/>
                                    Tolerated Warning Count : <span className='value'>{d.toleratedWarningCount}</span>
                                    <br/>
                                    Alert Count : <span className='value'>{d.alertCount}</span>
                                    <br/>
                                    Tolerated Alert Count : <span className='value'>{d.toleratedAlertCount}</span>
                                </StyledDetailDiv>
                            </TableCell>
                            <TableCell>
                                <Box>
                                    {/*<DownloadFileButton*/}
                                    {/*    url={ApiUrl.Published.getV1Report.getPath(d.id)}*/}
                                    {/*    title='Directory v1'*/}
                                    {/*/>*/}
                                    <DownloadFileButton
                                        url={ApiUrl.Published.getV2Report.getPath(d.id)}
                                        title='Directory V3.0'
                                    />
                                    <DownloadFileButton
                                        url={ApiUrl.Published.getAddReport.getPath(d.id)}
                                        title='Incremental Addition Directory'
                                    />
                                    <DownloadFileButton
                                        url={ApiUrl.Published.getInactiveReport.getPath(d.id)}
                                        title='Incremental Inactivation Directory'
                                    />
                                    <ReportMenu publicationId={d.id} publication={d} />
                                </Box>
                            </TableCell>
                            <TableCell sx={{ maxWidth: 450}}>
                                <Button component={Link} variant='text' color='primary' to={PortalUrl.Published.Publication.getPath(d.id)} >View Details</Button>
                                {d.detailsInfo &&
                                    <Box>
                                        {d.binaryCount > 0 && <div> <Button component={Link} to={PortalUrl.Published.HocReport.getPath(d.id)}> View {d.binaryCount} AdHoc Report{`${d.binaryCount > 1 ? 's': ''}`} </Button> </div> }



                                        {d.detailsInfo.publishedHISPs && Object.keys(d.detailsInfo.publishedHISPs).length > 0 &&
                                            <div> <Button onClick={() => handlePublishedHispsDialogOpen(d.detailsInfo.publishedHISPs)} > View Contributions </Button> </div>
                                        }
                                        {d.detailsInfo.failedHISPs && Object.keys(d.detailsInfo.failedHISPs).length > 0 &&
                                            <Box sx={{ backgroundColor: 'rgba(255, 0, 0, 0.15)', padding: 1, borderRadius: 2}}>
                                                <p style={{ padding: '0px 8px 0px 8px', marginBottom: '0px'}}>Failed HISPs</p>
                                                <ul style={{ marginTop: '5px'}}>
                                                    {Object.entries(d.detailsInfo.failedHISPs).map(([key, value]) => {
                                                        return (
                                                            <li key={key} style={{ margin: '4px'}}>
                                                                <span style={{color: 'red'}}>{key}</span>
                                                                :&nbsp;
                                                                <span>{value}</span>
                                                            </li>
                                                        )
                                                    })}
                                                </ul>
                                            </Box>
                                        }
                                    </Box>
                                }


                            </TableCell>
                        </StyledTableRow>
                    ))}
                </TableBody>
            </Table>
        </TableContainer>
    )
}


export const ReportMenu = (props) => {
    // publication carries the output paths, so an audience directory is only offered when the publish
    // actually produced one — publishes from before the split have both fields null.
    const { publicationId, publication } = props;

    const [reportMenuAnchorEl, setReportMenuAnchorEl] = useState(null);
    const reportMenuOpen = Boolean(reportMenuAnchorEl);


    const handleReportMenuClick = (event) => {
        setReportMenuAnchorEl(event.currentTarget);
    }

    const handleReportMenuClose = (event) => {
        const { id } = event.currentTarget;
        if (id === 'close' || id === '') {
            setReportMenuAnchorEl(null);
            return;
        }
        const entry = reportDownList[id] || audienceDownList[id];
        if (entry) {
            window.open(entry.url(publicationId))
        }
        setReportMenuAnchorEl(null);
    }


    return (
        <>
            <Button
                id='report-menu-button'
                aria-controls={open ? 'report-menu' : undefined}
                aria-haspopup='true'
                aria-expanded={open ? 'true' : undefined}
                onClick={handleReportMenuClick}
                endIcon={<ArrowDropDownIcon />}
                startIcon={<FileDownloadIcon fontSize='small' color='primary'/>}
                // size='small'
                sx={{ textTransform: 'none', fontSize: '15px'}}
            >
                Reports
            </Button>
            <Menu
                open={reportMenuOpen}
                anchorEl={reportMenuAnchorEl}
                onClose={handleReportMenuClose}
                sx={{
                    '& .MuiPaper-root': {
                        color: '#1769aa',
                        borderStyle: 'solid',
                        borderWidth: '0.4px' ,
                        boxShadow: 'rgb(255, 255, 255) 0px 0px 0px 0px, rgba(0, 0, 0, 0.05) 0px 0px 0px 1px, rgba(0, 0, 0, 0.1) 0px 10px 15px -3px, rgba(0, 0, 0, 0.05) 0px 4px 6px -2px'}}}
                MenuListProps={{
                    'aria-labelledby': 'report-menu-button'
                }}
            >
                <MenuList>
                    {/*<MenuItem*/}
                    {/*    id='close'*/}
                    {/*    onClick={handleReportMenuClose}*/}
                    {/*    sx={{ color: '#777777'}}*/}
                    {/*>*/}
                    {/*    <ListItemIcon>*/}
                    {/*        <CloseIcon fontSize='small'/>*/}
                    {/*    </ListItemIcon>*/}
                    {/*    <ListItemText sx={{ '& .MuiTypography-root': {fontSize: '0.875rem'}}}>Close the Menu</ListItemText>*/}
                    {/*</MenuItem>*/}
                    {Object.entries(reportDownList).map(([key, value]) => (
                        <Tooltip title={`Download ${value.content}`} arrow key={key} placement='left'>
                            <MenuItem
                                onClick={handleReportMenuClose}
                                id={key}
                            >
                                <ListItemIcon>
                                    <FileDownloadIcon fontSize='small' color='primary'/>
                                </ListItemIcon>
                                <ListItemText sx={{ '& .MuiTypography-root': {fontSize: '0.875rem'}}}>{value.content}</ListItemText>
                            </MenuItem>
                        </Tooltip>
                    ))}
                    {/* Audience directories, listed after the reports and only for a publish that has
                        them — a run from before the split leaves both paths null. */}
                    {Object.entries(audienceDownList)
                        .filter(([, value]) => publication && publication[value.pathField])
                        .map(([key, value]) => (
                        <Tooltip title={`Download the ${value.content}`} arrow key={key} placement='left'>
                            <MenuItem
                                onClick={handleReportMenuClose}
                                id={key}
                            >
                                <ListItemIcon>
                                    <FileDownloadIcon fontSize='small' color='info'/>
                                </ListItemIcon>
                                <ListItemText sx={{ '& .MuiTypography-root': {fontSize: '0.875rem'}}}>{value.content}</ListItemText>
                            </MenuItem>
                        </Tooltip>
                    ))}
                </MenuList>
            </Menu>
        </>
    )
}
